import { useMemo } from "react";
import { useTagPreferences } from "./useTagPreferences";
import { classifyItemTags } from "@/utils/tagClassifier";
/**
 * Split an array of items into "Most relevant to you" and "Remaining results"
 * based on the user's tag preferences stored in localStorage.
 *
 * @param items      — The full sorted list of items.
 * @param classifyFn — Extracts { disciplines, products, regions } from an item.
 *                     Defaults to `classifyItemTags` which handles most shapes.
 */
export function useRelevanceSplit(items, classifyFn = item => classifyItemTags(item)) {
  const {
    prefs
  } = useTagPreferences();
  return useMemo(() => {
    const hasPrefs = prefs.disciplines.length > 0 || prefs.products.length > 0 || prefs.regions.length > 0;
    if (!hasPrefs) {
      return {
        relevant: [],
        remaining: items,
        hasPrefs: false
      };
    }
    const prefDisc = new Set(prefs.disciplines);
    const prefProd = new Set(prefs.products);
    const prefReg = new Set(prefs.regions);
    const scored = items.map((item, idx) => {
      const classified = classifyFn(item);
      let score = 0;
      for (const d of classified.disciplines) if (prefDisc.has(d)) score++;
      for (const p of classified.products) if (prefProd.has(p)) score++;
      for (const r of classified.regions) if (prefReg.has(r)) score++;
      return {
        item,
        score,
        idx
      };
    });
    const relevant = scored.filter(s => s.score > 0).sort((a, b) => b.score - a.score || a.idx - b.idx).map(s => s.item);
    const remaining = scored.filter(s => s.score === 0).map(s => s.item);
    return {
      relevant,
      remaining,
      hasPrefs: true
    };
  }, [items, prefs, classifyFn]);
}